// Monday CRM integration settings.
// Path: client/src/views/account/monday_settings.js
//
// Mirrors the HubSpot integration setup but for Monday:
//  1. install the app in monday (new tab), then connect via OAuth (redirect
//     to the Monday consent screen),
//  2. pick a board and map its email/name/phone columns,
//  3. provision KYC Command / KYC Status / Verification Link columns + webhook,
//  4. manage the list of connected boards,
//  5. optionally upload each final verification PDF to a File column.
import React, { Fragment, useState, useEffect, useContext, useCallback } from "react";
import { Card, Button, Loader, ViewContext } from "components/lib";
import Axios from "axios";
import {
  MONDAY_PDF_OPTIONS,
  pdfOptionValues,
  fileColumns,
  selectedFileColumnId,
  boardPdfStatus,
  pdfColumnPayload,
} from "./monday-pdf-settings";

const labelClass = "block text-sm font-semibold mb-1 mt-3";
const inputClass =
  "w-full border border-gray-300 rounded px-3 py-2 text-sm bg-white";

// One connected board's PDF write-back column: loads the board's columns,
// offers its File columns, and saves the choice on its own.
function MondayBoardPdfRow({ board, notify }) {
  const [columns, setColumns] = useState([]);
  const [columnsLoading, setColumnsLoading] = useState(true);
  const [loadFailed, setLoadFailed] = useState(false);
  const [storedId, setStoredId] = useState(board.pdf_file_column_id || null);
  const [selected, setSelected] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setColumnsLoading(true);
      setLoadFailed(false);
      try {
        const res = await Axios.get(
          `/api/integration/monday/boards/${board.board_id}/columns`
        );
        if (cancelled) return;
        const cols = res?.data?.data || [];
        setColumns(cols);
        setStoredId(board.pdf_file_column_id || null);
        setSelected(selectedFileColumnId(cols, board.pdf_file_column_id));
      } catch (err) {
        if (!cancelled) setLoadFailed(true);
      } finally {
        if (!cancelled) setColumnsLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [board.board_id, board.pdf_file_column_id]);

  async function handleSave() {
    if (saving) return;
    setSaving(true);
    try {
      const res = await Axios.post(
        `/api/integration/monday/boards/${board.board_id}/pdf-column`,
        pdfColumnPayload(selected)
      );
      setStoredId(res?.data?.data?.pdf_file_column_id || null);
      notify(selected ? "PDF column saved" : "PDF column cleared");
    } catch (err) {
      notify(
        err?.response?.data?.message || "Could not save the PDF column",
        "error"
      );
    } finally {
      setSaving(false);
    }
  }

  const selectId = `monday-pdf-column-${board.board_id}`;
  const files = fileColumns(columns);
  const status = boardPdfStatus({ columns, storedId });
  // A saved column that was deleted in monday can still be cleared with None.
  const dirty = selected !== (storedId || "");

  let body;
  if (columnsLoading) {
    body = <Loader />;
  } else if (loadFailed) {
    body = (
      <p className="text-xs leading-6 text-red-700">
        Could not load this board's columns. Reload the page to try again.
      </p>
    );
  } else {
    body = (
      <Fragment>
        {files.length > 0 && (
          <select
            id={selectId}
            className={inputClass}
            value={selected}
            onChange={(e) => setSelected(e.target.value)}
          >
            <option value="">None</option>
            {files.map((c) => (
              <option key={c.id} value={c.id}>
                {c.title}
              </option>
            ))}
          </select>
        )}
        <p
          className={`text-xs leading-6 ${
            status.tone === "ok" ? "text-gray-500" : "text-red-700"
          }`}
        >
          {status.text}
        </p>
        {dirty && (
          <Button
            text={saving ? "Saving..." : "Save"}
            action={handleSave}
            loading={saving}
            small
          />
        )}
      </Fragment>
    );
  }

  return (
    <div className="border-b border-gray-100 py-2">
      <label className={labelClass} htmlFor={selectId}>
        {board.board_name || `Board ${board.board_id}`}
      </label>
      {body}
    </div>
  );
}

export function MondaySettingsView(props) {
  const viewContext = useContext(ViewContext);

  const [loading, setLoading] = useState(true);
  const [connected, setConnected] = useState(false);
  const [settings, setSettings] = useState(null);

  // add-board form
  const [boards, setBoards] = useState([]);
  const [boardsLoading, setBoardsLoading] = useState(false);
  const [selectedBoardId, setSelectedBoardId] = useState("");
  const [columns, setColumns] = useState([]);
  const [columnsLoading, setColumnsLoading] = useState(false);
  const [mapping, setMapping] = useState({
    email_column_id: "",
    first_name_column_id: "",
    last_name_column_id: "",
    phone_column_id: "",
  });
  const [provisioning, setProvisioning] = useState(false);
  const [installing, setInstalling] = useState(false);
  const [pdfEnabled, setPdfEnabled] = useState(false);
  const [pdfSaving, setPdfSaving] = useState(false);
  const [pdfOptions, setPdfOptions] = useState(pdfOptionValues(null));
  const [pdfOptionSaving, setPdfOptionSaving] = useState(false);
  // Tranche 2 column provisioning state, keyed by board id.
  const [t2Provisioning, setT2Provisioning] = useState({});

  const notify = (message, type = "success") =>
    viewContext.notification.show(message, type, true);

  const loadSettings = useCallback(async () => {
    setLoading(true);
    try {
      const connectedRes = await Axios.get("/api/integration/monday/connected");
      setConnected(connectedRes?.data?.data === true);

      const settingsRes = await Axios.get("/api/integration/monday/settings");
      setSettings(settingsRes?.data?.data || null);
      setPdfEnabled(settingsRes?.data?.data?.pdf_results_writeback === true);
      setPdfOptions(pdfOptionValues(settingsRes?.data?.data?.pdf_writeback_options));
    } catch (err) {
      setConnected(false);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadSettings();
  }, [loadSettings]);

  // Load the account's boards once connected (for the add-board dropdown).
  useEffect(() => {
    if (!connected) return;
    let cancelled = false;
    (async () => {
      setBoardsLoading(true);
      try {
        const res = await Axios.get("/api/integration/monday/boards");
        if (!cancelled) setBoards(res?.data?.data || []);
      } catch (err) {
        if (!cancelled) setBoards([]);
      } finally {
        if (!cancelled) setBoardsLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [connected]);

  // Open the monday installation page in a new tab. The tab opens before the
  // request because browsers block window.open after an await; the settings
  // tab stays open for the Connect step.
  async function handleInstall() {
    if (installing) return;
    const tab = window.open("", "_blank");
    if (!tab) {
      notify(
        "Your browser blocked the new tab. Allow pop-ups for this site, then try again.",
        "error"
      );
      return;
    }
    tab.opener = null;
    setInstalling(true);
    try {
      const res = await Axios.get("/api/integration/monday/install-link");
      const url = res?.data?.data?.url;
      if (!url) throw new Error("no installation link");
      tab.location.href = url;
    } catch (err) {
      tab.close();
      notify(
        err?.response?.data?.message || "Could not open the monday installation page",
        "error"
      );
    } finally {
      setInstalling(false);
    }
  }

  // Start the OAuth flow: fetch the consent URL and redirect (mirrors HubSpot).
  async function handleConnect() {
    try {
      const res = await Axios.get("/api/integration/monday/install");
      const url = res?.data?.data?.url;
      if (url) {
        window.location = url;
      } else {
        notify("Could not start the Monday connection", "error");
      }
    } catch (err) {
      notify(
        err?.response?.data?.message || "Could not start the Monday connection",
        "error"
      );
    }
  }

  async function handleSelectBoard(boardId) {
    setSelectedBoardId(boardId);
    setColumns([]);
    setMapping({
      email_column_id: "",
      first_name_column_id: "",
      last_name_column_id: "",
      phone_column_id: "",
    });
    if (!boardId) return;
    setColumnsLoading(true);
    try {
      const res = await Axios.get(
        `/api/integration/monday/boards/${boardId}/columns`
      );
      setColumns(res?.data?.data || []);
    } catch (err) {
      notify("Could not load the board's columns", "error");
    } finally {
      setColumnsLoading(false);
    }
  }

  async function handleProvision() {
    if (!selectedBoardId) {
      notify("Select a board first", "error");
      return;
    }
    if (!mapping.email_column_id) {
      notify("An email column mapping is required", "error");
      return;
    }
    setProvisioning(true);
    try {
      const board = boards.find((b) => `${b.id}` === `${selectedBoardId}`);
      await Axios.post("/api/integration/monday/boards", {
        board_id: selectedBoardId,
        board_name: board?.name,
        ...mapping,
      });
      notify("Board connected — KYC columns and webhook created");
      setSelectedBoardId("");
      setColumns([]);
      await loadSettings();
    } catch (err) {
      notify(
        err?.response?.data?.message || "Could not provision that board",
        "error"
      );
    } finally {
      setProvisioning(false);
    }
  }

  async function handleDisconnectBoard(boardId) {
    try {
      await Axios.delete(`/api/integration/monday/boards/${boardId}`);
      notify("Board disconnected");
      await loadSettings();
    } catch (err) {
      notify(
        err?.response?.data?.message || "Could not disconnect that board",
        "error"
      );
    }
  }

  // Turn the PDF write-back on or off. Saves at once and reverts on failure.
  async function handleTogglePdf(next) {
    if (pdfSaving) return;
    setPdfEnabled(next);
    setPdfSaving(true);
    try {
      await Axios.post("/api/integration/monday/pdf-writeback", { enabled: next });
      notify(next ? "PDF write-back turned on" : "PDF write-back turned off");
    } catch (err) {
      setPdfEnabled(!next);
      notify(
        err?.response?.data?.message || "Could not save the PDF write-back setting",
        "error"
      );
    } finally {
      setPdfSaving(false);
    }
  }

  // Save one PDF contents option. Saves at once and reverts on failure.
  async function handlePdfOption(key, value) {
    if (pdfOptionSaving) return;
    setPdfOptions((o) => ({ ...o, [key]: value }));
    setPdfOptionSaving(true);
    try {
      const res = await Axios.post("/api/integration/monday/pdf-options", {
        [key]: value,
      });
      setPdfOptions(pdfOptionValues(res?.data?.data?.pdf_writeback_options));
    } catch (err) {
      setPdfOptions((o) => ({ ...o, [key]: !value }));
      notify(
        err?.response?.data?.message || "Could not save the PDF option",
        "error"
      );
    } finally {
      setPdfOptionSaving(false);
    }
  }

  // Create the two Tranche 2 result columns on a connected board. Only offered
  // when the account has Tranche 2 enabled; idempotent server-side.
  async function handleProvisionTranche2Columns(boardId) {
    setT2Provisioning((m) => ({ ...m, [boardId]: true }));
    try {
      await Axios.post(
        `/api/integration/monday/boards/${boardId}/tranche2-columns`
      );
      notify("Tranche 2 columns created — Risk Assessment Status and Risk Assessment");
      await loadSettings();
    } catch (err) {
      notify(
        err?.response?.data?.message || "Could not create the Tranche 2 columns",
        "error"
      );
    } finally {
      setT2Provisioning((m) => ({ ...m, [boardId]: false }));
    }
  }

  if (loading) return <Loader />;

  // Columns the client can map. Email mapping is required; the others optional.
  const textLikeColumns = columns.filter((c) =>
    ["text", "email", "phone", "name", "long-text"].includes(c.type)
  );
  const mappableColumns = textLikeColumns.length ? textLikeColumns : columns;

  return (
    <Fragment>
      {!connected && (
        <Card title="Connect Monday">
          <p className="text-sm leading-6 mb-3">
            Connect your Monday account to trigger IdentityCheck
            verifications from your CRM boards. You'll be redirected to Monday to
            approve access; IdentityCheck never sees or stores your password or
            API token.
          </p>

          <p className="text-sm font-semibold">
            Step 1: Install the app in monday
          </p>
          <p className="text-xs leading-6 text-gray-500 mb-2">
            A monday admin must install the IdentityCheck app in your monday
            account before you connect. The install page opens in a new tab.
            Skip this step if the app is already installed.
          </p>
          <Button
            text={installing ? "Opening..." : "Install the app in monday"}
            title="Opens the monday installation page in a new tab"
            action={handleInstall}
            loading={installing}
            small
            outline
          />

          <p className="text-sm font-semibold mt-4">Step 2: Connect</p>
          <p className="text-xs leading-6 text-gray-500 mb-2">
            After the app is installed, connect to approve access.
          </p>
          <Button text="Connect Monday" action={handleConnect} small />
        </Card>
      )}

      {connected && (
        <Fragment>
          <Card title="Monday connection">
            <p className="text-sm leading-6">
              Status: <strong>Connected</strong>
            </p>
            <p className="text-xs leading-6 text-gray-500">
              Reconnect if access was revoked or you switched Monday accounts.
            </p>
            <br />
            <Button text="Reconnect" action={handleConnect} small outline />
          </Card>

          <Card title="Add a board">
            {boardsLoading ? (
              <Loader />
            ) : (
              <Fragment>
                <label className={labelClass}>Board</label>
                <select
                  className={inputClass}
                  value={selectedBoardId}
                  onChange={(e) => handleSelectBoard(e.target.value)}
                >
                  <option value="">Select a board…</option>
                  {boards.map((b) => (
                    <option key={b.id} value={b.id}>
                      {b.name}
                    </option>
                  ))}
                </select>

                {columnsLoading && <Loader />}

                {!columnsLoading && columns.length > 0 && (
                  <Fragment>
                    {[
                      { key: "email_column_id", label: "Email column (required)" },
                      { key: "first_name_column_id", label: "First name column" },
                      { key: "last_name_column_id", label: "Last name column" },
                      { key: "phone_column_id", label: "Phone column" },
                    ].map((field) => (
                      <Fragment key={field.key}>
                        <label className={labelClass}>{field.label}</label>
                        <select
                          className={inputClass}
                          value={mapping[field.key]}
                          onChange={(e) =>
                            setMapping((m) => ({ ...m, [field.key]: e.target.value }))
                          }
                        >
                          <option value="">
                            {field.key === "email_column_id"
                              ? "Select the email column…"
                              : "None"}
                          </option>
                          {mappableColumns.map((c) => (
                            <option key={c.id} value={c.id}>
                              {c.title} ({c.type})
                            </option>
                          ))}
                        </select>
                      </Fragment>
                    ))}
                    <br />
                    <br />
                    <Button
                      text={provisioning ? "Provisioning..." : "Connect board"}
                      action={handleProvision}
                      loading={provisioning}
                      small
                    />
                  </Fragment>
                )}
              </Fragment>
            )}
          </Card>

          <Card title="Connected boards">
            {settings?.boards?.length ? (
              settings.boards.map((b) => {
                const tranche2ColumnsCreated =
                  !!b.risk_assessment_status_column_id &&
                  !!b.risk_assessment_column_id;
                return (
                  <div
                    key={b.board_id}
                    className="border-b border-gray-100 py-2"
                  >
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm font-semibold">
                          {b.board_name || `Board ${b.board_id}`}
                        </p>
                        <p className="text-xs text-gray-500">
                          Board {b.board_id}
                          {b.webhook_id ? ` · webhook ${b.webhook_id}` : ""}
                        </p>
                      </div>
                      <Button
                        text="Disconnect"
                        action={() => handleDisconnectBoard(b.board_id)}
                        small
                      />
                    </div>

                    {/* Tranche 2 write-back: only shown when the account has
                        Tranche 2 enabled. */}
                    {settings?.tranche2_enabled && (
                      <div className="mt-2 flex items-center justify-between">
                        <p className="text-xs text-gray-500">
                          Tranche 2 columns:{" "}
                          <strong>
                            {tranche2ColumnsCreated ? "Created" : "Not created"}
                          </strong>
                        </p>
                        {!tranche2ColumnsCreated && (
                          <Button
                            text={
                              t2Provisioning[b.board_id]
                                ? "Creating..."
                                : "Create Tranche 2 columns"
                            }
                            action={() =>
                              handleProvisionTranche2Columns(b.board_id)
                            }
                            loading={!!t2Provisioning[b.board_id]}
                            small
                          />
                        )}
                      </div>
                    )}
                  </div>
                );
              })
            ) : (
              <p className="text-sm leading-6">
                No boards connected yet. Add one above to start triggering
                verifications from Monday.
              </p>
            )}
          </Card>

          <Card title="PDF write-back">
            <p className="text-sm leading-6 mb-3">
              When a verification started from a connected board reaches a
              final decision, IdentityCheck uploads the verification report PDF
              to that item's File column. Each decision adds a file.
              IdentityCheck never removes files from the column. A section
              prints only when its check ran.
            </p>
            <label className="flex items-center text-sm font-semibold cursor-pointer">
              <input
                type="checkbox"
                className="mr-2"
                checked={pdfEnabled}
                disabled={pdfSaving}
                onChange={(e) => handleTogglePdf(e.target.checked)}
              />
              Upload the verification PDF to monday
            </label>

            {pdfEnabled && (
              <div className="mt-3">
                <p className="text-sm font-semibold">What the PDF includes</p>
                <p className="text-xs leading-6 text-gray-500">
                  Every PDF has the summary page: the person's name and date of
                  birth, and the verification result. Choose what else to add.
                </p>
                {MONDAY_PDF_OPTIONS.map((option) => (
                  <label
                    key={option.key}
                    className="flex items-center text-sm cursor-pointer"
                  >
                    <input
                      type="checkbox"
                      className="mr-2"
                      checked={pdfOptions[option.key]}
                      disabled={pdfOptionSaving}
                      onChange={(e) => handlePdfOption(option.key, e.target.checked)}
                    />
                    {option.label}
                  </label>
                ))}
              </div>
            )}

            {pdfEnabled &&
              (settings?.boards?.length ? (
                <div className="mt-3">
                  <p className="text-xs leading-6 text-gray-500">
                    Choose a File column for each board. A board without a
                    column does not upload.
                  </p>
                  {settings.boards.map((b) => (
                    <MondayBoardPdfRow key={b.board_id} board={b} notify={notify} />
                  ))}
                </div>
              ) : (
                <p className="text-xs leading-6 text-gray-500 mt-2">
                  Add a board above, then choose its File column here.
                </p>
              ))}
          </Card>
        </Fragment>
      )}
    </Fragment>
  );
}
